import { conformToMask } from 'react-text-mask';

import { LANGUAGES } from 'state/intl/types';
import { format } from 'utils/language/format-date';

const localizedDateTimeFormats = {
  'en-US': {
    date: 'MMMM d, yyyy',
    time: 'h:m aaaa',
  },
  'en-CA': {
    date: 'MMM d, yyyy',
    time: 'h:m aaaa',
  },
};

interface ILocalizedDateTime {
  date: string;
  time: string;
}

export const localizedDateTimeFormatter = (
  dateTime: string,
  locale: string
): ILocalizedDateTime => {
  const splitDateTime = dateTime.split(/\s/);
  const date = splitDateTime[0];
  const time = splitDateTime.slice(1).join(' ');

  /**
   * We're tacking on this 00:00 so that `Date` interprets the input `date` correctly
   * Without this, `2020-01-28` could be interpreted as `January 27, 2020`.
   *
   * Ideally, we'd just pass in the whole `dateTime`, but that's improperly formatted,
   * as identified here: https://rbictg.atlassian.net/browse/CBA-4083
   */
  const formattedDate = format(
    new Date(`${date}T00:00`),
    localizedDateTimeFormats[locale].date,
    LANGUAGES.en
  );

  return {
    date: formattedDate,
    time,
  };
};

export const getFormattedLongDateFromIsoString = (
  isoDateTime: string,
  formatMessage: Function
): string =>
  new Date(isoDateTime).toLocaleString(formatMessage({ id: 'localeDateTime' }), {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

export const getFormattedShortDateFromIsoString = (
  isoDateTime: string,
  formatMessage: Function
): string =>
  new Date(isoDateTime).toLocaleString(formatMessage({ id: 'localeDateTime' }), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

export const getFormattedTimeFromIsoString = (
  isoDateTime: string,
  formatMessage: Function
): string => {
  return new Date(isoDateTime).toLocaleString(formatMessage({ id: 'localeDateTime' }), {
    hour: 'numeric',
    minute: 'numeric',
  });
};

export const getFormattedTimeFromIsoStringWithTimezone = (
  isoDateTime: string,
  timezone: string | null | undefined,
  formatMessage: Function
): string => {
  return new Date(isoDateTime).toLocaleString(formatMessage({ id: 'localeDateTime' }), {
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
    ...(timezone && { timeZone: timezone }), // fallback to user timezone
  });
};

export const getFormattedDateAtTime = (
  { date, time }: { date: string; time: string },
  formatMessage: Function
) =>
  date && time
    ? formatMessage(
        { id: 'dateAtTime' },
        {
          date,
          time,
        }
      )
    : '';

const localizedCountryCodes = {
  'en-US': '+1',
  'en-CA': '+1',
};

// ex. `9543214567` -> `(954) 321 4567`
const phoneNumberMask = [
  '(',
  /\d/,
  /\d/,
  /\d/,
  ')',
  ' ',
  /\d/,
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

export const formatPhoneNumber = (phoneNumber: string, locale: string): string => {
  const phoneNumberWithoutCountryCode = phoneNumber?.replace(localizedCountryCodes[locale], '');
  const { conformedValue } = conformToMask(phoneNumberWithoutCountryCode, phoneNumberMask, {});
  return conformedValue;
};
